/* eslint-disable @typescript-eslint/no-unused-vars */
import { Capacitor } from '@capacitor/core';
import { Purchases, LOG_LEVEL, PACKAGE_TYPE, PurchasesPackage } from '@revenuecat/purchases-capacitor';

export interface AndroidPurchaseResult {
  customerInfo: any;
  productIdentifier: string;
  success: boolean;
  outcome?: 'cancelled' | 'pending' | 'error';
}

class AndroidPaymentService {
  private isInitialized = false;

  async initialize(): Promise<void> {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') {
      console.log('🤖 AndroidPaymentService: Not on Android platform, skipping initialization');
      return;
    }

    if (this.isInitialized) {
      console.log('🤖 AndroidPaymentService: Already initialized');
      return;
    }

    try {
      // Initialize RevenueCat with your public Android SDK key
      // This is from the SAME RevenueCat project as iOS - just the Android app's public key
      // Get it from: RevenueCat Dashboard → Your Project → Android App → Public API Key
      const ANDROID_SDK_KEY = 'goog_qYqZyaVwGSuzdJeRbjGwofticwx';
      
      await Purchases.configure({
        apiKey: ANDROID_SDK_KEY,
        appUserID: undefined, // Will be set when user logs in
      });

      await Purchases.setLogLevel({ level: process.env.NODE_ENV === 'production' ? LOG_LEVEL.ERROR : LOG_LEVEL.DEBUG });
      
      this.isInitialized = true;
      console.log('🤖 AndroidPaymentService: Initialized successfully');
    } catch (error) {
      console.error('🤖 AndroidPaymentService: Initialization failed:', error);
      throw error;
    }
  }

  async setUserId(userId: string): Promise<void> {
    if (!userId.trim()) throw new Error('Sign in before subscribing.');
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      await Purchases.logIn({ appUserID: userId });
    } catch (error) {
      console.error('🤖 AndroidPaymentService: Failed to set user ID:', error);
      throw error;
    }
  }

  async getAvailablePackages(): Promise<PurchasesPackage[]> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      const offerings = await Purchases.getOfferings();
      console.log('🤖 AndroidPaymentService: Available offerings:', offerings);

      if (offerings.current) {
        const packages = offerings.current.availablePackages || [];
        console.log('🤖 AndroidPaymentService: Available packages:', packages);
        return packages;
      }

      return [];
    } catch (error) {
      console.error('🤖 AndroidPaymentService: Failed to get packages:', error);
      return [];
    }
  }

  async purchasePackage(packageToPurchase: PurchasesPackage): Promise<AndroidPurchaseResult> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      console.log('🤖 AndroidPaymentService: Starting purchase for package:', packageToPurchase.identifier);
      
      const result = await Purchases.purchasePackage({ 
        aPackage: packageToPurchase 
      });


      return {
        customerInfo: result.customerInfo,
        productIdentifier: packageToPurchase.product.identifier,
        success: true
      };
    } catch (error) {
      console.error('🤖 AndroidPaymentService: Purchase failed:', error);
      return {
        customerInfo: null,
        productIdentifier: packageToPurchase.product.identifier,
        success: false,
        outcome: String((error as any)?.code) === '1' || (error as any)?.userCancelled ? 'cancelled' : String((error as any)?.code) === '20' ? 'pending' : 'error'
      };
    }
  }

  async restorePurchases(userId: string): Promise<any> {
    // Bind restoration to the current account before contacting Google Play.
    await this.setUserId(userId);

    try {
      console.log('🤖 AndroidPaymentService: Restoring purchases...');
      const { customerInfo } = await Purchases.restorePurchases();
      return customerInfo;
    } catch (error) {
      console.error('🤖 AndroidPaymentService: Failed to restore purchases:', error);
      throw error;
    }
  }

  async getCustomerInfo(): Promise<any> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      const { customerInfo } = await Purchases.getCustomerInfo();
      return customerInfo;
    } catch (error) {
      console.error('🤖 AndroidPaymentService: Failed to get customer info:', error);
      return null;
    }
  }

  async checkPremiumStatus(): Promise<'free' | 'premium'> {
    try {
      const customerInfo = await this.getCustomerInfo();
      
      if (customerInfo?.entitlements?.active?.premium) {
        console.log('🤖 AndroidPaymentService: User has premium entitlement');
        return 'premium';
      }

      console.log('🤖 AndroidPaymentService: User does not have premium entitlement');
      return 'free';
    } catch (error) {
      console.error('🤖 AndroidPaymentService: Failed to check premium status:', error);
      return 'free';
    }
  }

  isAvailable(): boolean {
    return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
  }
}

export const androidPaymentService = new AndroidPaymentService();
export default androidPaymentService;
